import { ArrowLeftIcon, LockSimpleIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

import type { ManualStep } from "../types";

type Props = {
  children: ReactNode;
  step: ManualStep;
  illustration: string;
  message: string;
  busy?: boolean;
  onClose: () => void;
  onBack?: () => void;
};

export const OnboardingShell = ({
  children,
  step,
  illustration,
  message,
  busy = false,
  onClose,
  onBack,
}: Props) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-resume-title"
      className={cn(
        "fixed inset-0 z-[100]",
        "flex items-center justify-center overflow-hidden",
        "bg-slate-950/55 backdrop-blur-sm",
        "sm:p-4",
      )}
    >
      <div
        data-step={step}
        className={cn(
          "relative grid h-[100dvh] w-full overflow-hidden bg-white",
          "shadow-[0_24px_70px_rgba(15,23,42,0.24)]",
          "sm:h-[min(94dvh,850px)]",
          "sm:w-[calc(100vw-2rem)]",
          "sm:max-w-[1400px]",
          "sm:rounded-[28px]",
          "sm:border sm:border-indigo-100",
          "lg:grid-cols-[minmax(0,1fr)_36%]",
        )}
      >
        <header
          className={cn(
            "absolute inset-x-0 top-0 z-30",
            "flex h-14 items-center justify-between",
            "px-4 sm:h-[72px] sm:px-7 lg:px-9",
          )}
        >
          <img
            src="/logo/mon-cv-pro_logo.svg"
            alt="Mon CV Pro"
            draggable={false}
            className="h-10 w-auto object-contain sm:h-12"
          />

          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            aria-label="Quitter la création du CV"
            className={cn(
              "inline-flex min-h-10 items-center gap-2",
              "rounded-xl px-2.5",
              "text-xs font-bold text-indigo-950",
              "transition-colors hover:bg-indigo-50",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "sm:text-sm",
            )}
          >
            <XIcon aria-hidden size={24} weight="bold" />
          </button>
        </header>

        <main
          className={cn(
            "flex min-h-0 min-w-0 flex-col overflow-hidden",
            "px-4 pb-3 pt-14",
            "sm:px-7 sm:pb-4 sm:pt-[72px]",
            "lg:px-10 lg:pb-5",
            "xl:px-14",
          )}
        >
          <div
            className={cn(
              "mx-auto flex min-h-0 w-full max-w-3xl flex-1",
              "flex-col overflow-hidden",
            )}
          >
            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden",
                "overscroll-contain pr-1",
                "[scrollbar-gutter:stable]",
                "[scrollbar-color:rgb(129_140_248)_transparent]",
                "[scrollbar-width:thin]",
                "[&::-webkit-scrollbar]:w-1.5",
                "[&::-webkit-scrollbar-track]:rounded-full",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:rounded-full",
                "[&::-webkit-scrollbar-thumb]:bg-indigo-300",
                "[&::-webkit-scrollbar-thumb]:transition-colors",
                "[&::-webkit-scrollbar-thumb:hover]:bg-indigo-400",
                "sm:pr-2",
              )}
            >
              <div className={cn("my-auto w-full shrink-0", "py-2 sm:py-3")}>{children}</div>
            </div>
          </div>

          <footer
            className={cn(
              "flex shrink-0 items-center justify-between gap-3",
              "border-t border-slate-100",
              "pt-2 sm:pt-3",
            )}
          >
            {onBack ? (
              <button
                type="button"
                disabled={busy}
                onClick={onBack}
                className={cn(
                  "inline-flex min-h-10 shrink-0",
                  "items-center gap-2 rounded-xl",
                  "border-2 border-indigo-600 px-4",
                  "text-xs font-bold text-indigo-700",
                  "transition-colors hover:bg-indigo-50",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "sm:min-h-11 sm:px-5 sm:text-sm",
                )}
              >
                <ArrowLeftIcon aria-hidden size={17} weight="bold" />
                Retour
              </button>
            ) : (
              <span />
            )}

            <p
              className={cn(
                "flex min-w-0 items-center justify-end gap-1.5",
                "text-right text-[8px] font-medium text-slate-400",
                "sm:text-[10px] lg:text-[11px]",
              )}
            >
              <LockSimpleIcon aria-hidden size={14} weight="fill" className="shrink-0" />

              <span className="xs:inline hidden">
                Vos réponses servent uniquement à personnaliser votre parcours.
              </span>
            </p>
          </footer>
        </main>

        <aside
          className={cn(
            "relative hidden min-h-0 overflow-hidden",
            "items-end justify-center",
            "bg-gradient-to-b from-violet-50/30 via-white to-white",
            "px-4 pt-[72px]",
            "lg:flex",
          )}
        >
          <div
            aria-hidden
            className={cn(
              "absolute bottom-[8%] left-1/2",
              "h-[66%] w-[82%] -translate-x-1/2",
              "rounded-[50%] bg-violet-100/60",
              "blur-[1px]",
            )}
          />

          {message && (
            <div
              className={cn(
                "absolute left-4 top-[29%] z-20",
                "max-w-[190px]",
                "xl:left-7 xl:max-w-[220px]",
              )}
            >
              <div
                className={cn(
                  "relative",
                  "rounded-bl-2xl rounded-tl-2xl rounded-tr-2xl",
                  "rounded-br-none",
                  "border border-indigo-300 bg-white",
                  "px-4 py-3 text-center",
                  "text-xs font-semibold leading-5 text-indigo-950",
                  "shadow-[0_12px_28px_rgba(30,27,75,0.13)]",
                  "xl:px-5 xl:text-sm",
                )}
              >
                {message}

                <span
                  aria-hidden
                  className={cn(
                    "pointer-events-none absolute",
                    "-bottom-[18px] -right-px",
                    "h-0 w-0",
                    "border-l-[28px] border-l-transparent",
                    "border-t-[19px] border-t-indigo-300",
                  )}
                />

                <span
                  aria-hidden
                  className={cn(
                    "pointer-events-none absolute",
                    "-bottom-[15px] right-0",
                    "h-0 w-0",
                    "border-l-[24px] border-l-transparent",
                    "border-t-[16px] border-t-white",
                  )}
                />
              </div>
            </div>
          )}

          <img
            src={illustration}
            alt="Votre guide Mon CV Pro"
            draggable={false}
            className={cn("relative z-10 w-full object-contain object-bottom", "max-h-[70%]")}
          />
        </aside>
      </div>
    </div>
  );
};
